import KybRetentionRepository from "@/core/kyb/repositories/KybRetentionRepository";
import {
  KybCaseRetention,
  KybRetention,
  KybRetentionDecision,
  KybRetentionDecisionInput,
  KybRetentionDecisionType,
  KybRetentionRegisterEntry,
  KybRetentionStatus,
} from "@/core/kyb/entities/KybRetention";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest } from "../../http/services/kybRequest/kybRequest";
import kybRetentionEndpoints from "../../api/kybRetentionEndpoints/kybRetentionEndpoints";

type RetentionDto = {
  caseId: string;
  baseRetainUntil: string;
  retainUntil: string;
  daysRemaining: number;
  status: KybRetentionStatus;
  legalHold: boolean;
  holdPlacedBy?: string | null;
  deletionRequestedBy?: string | null;
};

type RegisterEntryDto = { retention: RetentionDto; businessKey?: string | null; legalEntityName?: string | null };

type DecisionDto = {
  decisionId: string;
  type: KybRetentionDecisionType;
  previousRetainUntil?: string | null;
  newRetainUntil?: string | null;
  reason: string;
  legalBasis?: string | null;
  decidedBy: string;
  decidedAt: string;
};

type CaseRetentionDto = { retention?: RetentionDto | null; decisions: DecisionDto[] };
type OutcomeDto = { decision: DecisionDto; retention: RetentionDto };

/** Le back rend `null` pour une valeur absente : l'écran travaille avec des champs optionnels. */
const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

const mapRetention = (dto: RetentionDto): KybRetention => ({
  caseId: dto.caseId,
  baseRetainUntil: dto.baseRetainUntil,
  retainUntil: dto.retainUntil,
  daysRemaining: dto.daysRemaining,
  status: dto.status,
  legalHold: dto.legalHold,
  holdPlacedBy: orUndefined(dto.holdPlacedBy),
  deletionRequestedBy: orUndefined(dto.deletionRequestedBy),
});

const mapDecision = (dto: DecisionDto): KybRetentionDecision => ({
  decisionId: dto.decisionId,
  type: dto.type,
  previousRetainUntil: orUndefined(dto.previousRetainUntil),
  newRetainUntil: orUndefined(dto.newRetainUntil),
  reason: dto.reason,
  legalBasis: orUndefined(dto.legalBasis),
  decidedBy: dto.decidedBy,
  decidedAt: dto.decidedAt,
});

/** Corps attendu par le back : seule la prolongation porte une date, seules trois décisions portent une base légale. */
function bodyOf(input: KybRetentionDecisionInput): Record<string, string> {
  switch (input.action) {
    case "EXTEND":
      return { newRetainUntil: input.newRetainUntil ?? "", reason: input.reason };
    case "RELEASE":
      return { reason: input.reason };
    default:
      return { reason: input.reason, legalBasis: input.legalBasis ?? "" };
  }
}

export default class KybRetentionRepositoryBackend implements KybRetentionRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async listDue(days: number, limit: number): Promise<RepositoryResult<KybRetentionRegisterEntry[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<RegisterEntryDto[]>>({
          url: kybRetentionEndpoints.register(days, limit),
          withAuth: true,
        }),
      (dtos) =>
        dtos.map((dto) => ({
          retention: mapRetention(dto.retention),
          businessKey: orUndefined(dto.businessKey),
          legalEntityName: orUndefined(dto.legalEntityName),
        })),
    );
  }

  async getCase(caseId: string): Promise<RepositoryResult<KybCaseRetention>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<CaseRetentionDto>>({
          url: kybRetentionEndpoints.caseRetention(caseId),
          withAuth: true,
        }),
      (dto) => ({
        retention: dto.retention ? mapRetention(dto.retention) : undefined,
        decisions: dto.decisions.map(mapDecision),
      }),
    );
  }

  async decide(
    caseId: string,
    input: KybRetentionDecisionInput,
  ): Promise<RepositoryResult<{ decision: KybRetentionDecision; retention: KybRetention }>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<OutcomeDto>, Record<string, string>>({
          url: kybRetentionEndpoints.decision(caseId, input.action),
          body: bodyOf(input),
          withAuth: true,
        }),
      (dto) => ({ decision: mapDecision(dto.decision), retention: mapRetention(dto.retention) }),
    );
  }
}
